import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { HubSiteData } from '@app/models/hubsite-data';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { CacheService } from '@app/services/cache.service';
import { ChannelService } from '@app/services/channel.service';
import { TaxonomyService } from '@app/services/taxonomy.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { Environment } from '@environments/interface.environment';
import { combineLatest, concat, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  constructor(private http: HttpClient,
              private cache: CacheService,
              @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private channelService: ChannelService,
              private taxonomyService: TaxonomyService,
              private userChannelService: UserChannelTermsService,
              private httpHelper: HttpHelper,
              private featureSettingsStoreService: FeatureSettingsStoreService) { }

  private readonly cacheKeyPrefix = 'NavigationService';
  public useCache = true;
  private getNavigationNodes$: Observable<Array<SimplifiedTerm>>;
  private getHubSiteNavigation$: Observable<HubSiteData>;
  private getHubSiteNavigation_LastUsedUrl = '';

  getNavigationNodes(): Observable<Array<SimplifiedTerm>> {
    if (this.getNavigationNodes$) {
      return this.getNavigationNodes$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getNavigationTermSetNodes`;
      this.getNavigationNodes$ = this.taxonomyService.getTermSet(this.featureSettingsStoreService.state.navigationTermSet || Constants.NAVIGATION_TERMSET_GUID)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getNavigationNodes', result: new Array<SimplifiedTerm>() })),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<SimplifiedTerm>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getNavigationNodes$);
        }
      }
      return this.getNavigationNodes$;
    }
  }

  getHubSiteNavigation(hubSiteUrl: string): Observable<HubSiteData> {
    if (this.getHubSiteNavigation$) {
      return this.getHubSiteNavigation$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getHubSiteNavigation-${hubSiteUrl}`;
      this.getHubSiteNavigation$ = this.http.get<any>(`${hubSiteUrl}/_api/web/HubSiteData(true)`)
        .pipe(
          map(result => {
            console.log(`fetched hub site navigation: `);
            console.log(result);
            return new HubSiteData(JSON.parse(result.value));
          }),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getHubSiteNavigation', result: new HubSiteData() })),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<HubSiteData>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getHubSiteNavigation$);
        }
      }
      return this.getHubSiteNavigation$;
    }
  }

  // Figures out the home hub site for the user. Configured in term store using channels - see manual how to configure.
  getUsersHomeHubSiteUrl(): Observable<string> {
    return combineLatest([
      this.channelService.getChannels(),
      this.userChannelService.getUserChannelTerms()])
      .pipe(
        map(([channels, userChannelTerms]) => {

          // find channel containing with list of hub sites
          const siteListTerm = channels.filter(c => c.LocalCustomProperties[Constants.SETTINGS_CHANNEL_TERM_HUB_SITE_HOME_LIST] !== undefined && c.LocalCustomProperties[Constants.SETTINGS_CHANNEL_TERM_HUB_SITE_HOME_LIST].toLowerCase() === 'true');
          if (siteListTerm && siteListTerm.length > 0) {

            // get all my channels
            const myTerms = userChannelTerms.map(c => c.ChannelTermId.toLowerCase());

            // inner join to find *my* home site
            const foundHomeSite = siteListTerm[0].Terms.filter(t => myTerms.includes(t.Guid.toLowerCase()));

            // get url from term
            if (foundHomeSite && foundHomeSite.length > 0) {
              const homeSiteUrl = foundHomeSite[0].LocalCustomProperties[Constants.SETTINGS_CHANNEL_TERM_HUB_SITE_URL];
              if (homeSiteUrl) {

                // force fetching new data if home site is changed
                if (this.getHubSiteNavigation_LastUsedUrl !== homeSiteUrl) {
                  this.getHubSiteNavigation$ = undefined;
                }
                this.getHubSiteNavigation_LastUsedUrl = homeSiteUrl;

                return homeSiteUrl;
              }
            }
          }

          return this.environment.coreWorkplaceSiteUrl; // always return default
        })
      );
  }
}
